/**
 * BasecampRenderer — 카드형 (그리드)
 *
 * 그리드 레이아웃. 카드마다 본문 첫 이미지(extractFirstImageUrl) 가 있으면 썸네일로 표시,
 * 없으면 plain_text 미리보기로 fallback. Post 모델 변경 없이 content JSON 에서 자동 추출.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import { cn } from '../../../lib/utils';
import {
  Eye, MessageCircle, Pin, Megaphone,
} from 'lucide-react';
import { StyleRendererProps, timeAgo, CommentBadge, extractFirstImageUrl, NotifDot } from './shared';
import { UserAvatar } from '../../components/UserAvatar';

export const BasecampRenderer: React.FC<StyleRendererProps> = ({
  posts, onPostClick, selectedPostId, unreadPostIds, notifiedPostIds,
}) => (
  <div className="h-full overflow-y-auto px-4 lg:px-5 py-4">
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
      {posts.map(post => {
        const thumb = extractFirstImageUrl(post.content);
        const isSelected = selectedPostId === post.id;
        return (
          <button
            key={post.id}
            onClick={() => onPostClick(post)}
            className={cn(
              'group text-left rounded-xl border border-border/80 overflow-hidden flex flex-col',
              isSelected
                ? 'bg-accent border-accent-foreground/10'
                : 'bg-background hover:border-primary/25 hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)]',
              'active:scale-[0.995] transition-all duration-200 cursor-pointer',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              post.is_pinned && !isSelected && 'border-primary/20 bg-primary/[0.015]',
            )}
          >
            {/* 썸네일 영역 — 본문 첫 이미지가 있으면 표시, 없으면 텍스트 미리보기로 fallback */}
            {thumb ? (
              <div className="relative aspect-[16/9] bg-muted overflow-hidden">
                <img
                  src={thumb}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
                {/* 핀/공지 뱃지 오버레이 */}
                {(post.is_pinned || post.is_announcement) && (
                  <div className="absolute top-2 left-2 flex gap-1">
                    {post.is_pinned && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-primary/90 text-on-primary-text text-meta-sm font-semibold">
                        <Pin className="size-3" /><Trans>Pinned</Trans>
                      </span>
                    )}
                    {post.is_announcement && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-warning/90 text-on-warning-text text-meta-sm font-semibold">
                        <Megaphone className="size-3" /><Trans>Notice</Trans>
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // 썸네일 없음 → plain_text 본문 미리보기 (5줄) 영역
              <div className="px-4 pt-4 pb-2 min-h-[120px] bg-muted/30">
                <div className="flex items-center gap-2 mb-2">
                  {post.is_pinned && <Pin className="size-3.5 text-primary fill-primary/30 shrink-0" />}
                  {post.is_announcement && <Megaphone className="size-3.5 text-warning shrink-0" />}
                </div>
                {post.plain_text ? (
                  <p className="text-label text-muted-foreground/90 line-clamp-5 leading-relaxed">
                    {post.plain_text}
                  </p>
                ) : (
                  <p className="text-label text-muted-foreground/40 italic">(<Trans>No content</Trans>)</p>
                )}
              </div>
            )}

            {/* 본문 영역 */}
            <div className="p-4 flex-1 flex flex-col gap-2">
              {/* 제목 */}
              <h3 className="text-body font-semibold text-foreground line-clamp-2 leading-snug">
                {post.title}
                {notifiedPostIds?.has(post.id) && <span className="ml-1.5 align-middle"><NotifDot /></span>}
              </h3>

              {/* 썸네일 있을 때만 본문 한 줄 표시 (썸네일 없을 땐 위에서 표시) */}
              {thumb && post.plain_text && (
                <p className="text-meta text-muted-foreground line-clamp-2 leading-relaxed">
                  {post.plain_text}
                </p>
              )}

              {/* 태그 */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {post.tags.slice(0, 3).map(t => (
                    <span key={t} className="text-meta-sm px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">#{t}</span>
                  ))}
                </div>
              )}

              {/* 메타 (mt-auto 로 카드 하단 정렬) */}
              <div className="flex items-center gap-2 text-meta-sm text-muted-foreground mt-auto pt-1">
                <UserAvatar src={post.author.photoUrl ?? null} name={post.author.name} className="size-7 shrink-0" />
                <span className="font-medium text-foreground/70 truncate">{post.author.name}</span>
                <span className="opacity-40">·</span>
                <span className="shrink-0">{timeAgo(post.created_at)}</span>
                <span className="opacity-40">·</span>
                <CommentBadge count={post.comment_count} isUnread={unreadPostIds?.has(post.id)} variant="icon" />
                <span className="flex items-center gap-0.5 shrink-0"><Eye className="size-3" />{post.view_count}</span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);
